// =============================================================================
// MeuDriver — Hook: useDataPortability (Batch S4)
// Portabilidade de dados — LGPD Art. 18
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────

export type DataExportStatus = 'pending' | 'processing' | 'ready' | 'delivered'

export interface DataExportRequest {
  id: string
  user_id: string
  status: DataExportStatus
  download_url: string | null
  expires_at: string | null
  created_at: string
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const dataPortabilityKeys = {
  requests: ['data-export-requests'] as const,
}

// ─── useDataExportRequests ────────────────────────────────────────────────────

export function useDataExportRequests() {
  const { user } = useAuth()

  return useQuery<DataExportRequest[]>({
    queryKey: [...dataPortabilityKeys.requests, user?.id],
    enabled:  Boolean(user?.id),
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('data_export_requests')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw new Error(error.message)
      return (data ?? []) as DataExportRequest[]
    },
  })
}

// ─── useRequestDataExport ─────────────────────────────────────────────────────

export function useRequestDataExport() {
  const queryClient = useQueryClient()

  return useMutation<string, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('request_data_export')
      if (error) throw new Error(error.message)
      return data as string
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataPortabilityKeys.requests })
    },
  })
}

// ─── useGetMyData ─────────────────────────────────────────────────────────────
// Busca e retorna todos os dados do usuário via RPC.
// O download é feito client-side: o componente usa os dados retornados
// para gerar um Blob JSON e criar um link de download temporário.

export function useGetMyData() {
  return useMutation<Record<string, unknown>, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('get_my_data_export')
      if (error) throw new Error(error.message)
      return data as Record<string, unknown>
    },
  })
}

// ─── downloadJsonBlob ─────────────────────────────────────────────────────────
// Utilitário para disparar o download de um objeto JSON como arquivo .json

export function downloadJsonBlob(data: unknown, filename: string): void {
  const json    = JSON.stringify(data, null, 2)
  const blob    = new Blob([json], { type: 'application/json' })
  const url     = URL.createObjectURL(blob)
  const anchor  = document.createElement('a')
  anchor.href   = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
